/**
 * Entidades financieras del Perú donde un trabajador de campo puede tener su cuenta de haberes.
 *
 * POR QUÉ ES UNA LISTA Y NO TEXTO LIBRE: el `bank` que se escribe acá viaja **verbatim** a la
 * columna «Banco» de la planilla de pago que recibe finanzas. Escrito a mano, el mismo banco
 * llegaba de tres formas —«BCP», «Banco de Crédito del Perú», «bcp»— y alguien tenía que
 * adivinar; se ve en el propio archivo de julio del cliente, que trae las tres.
 *
 * ⚠️ **`valor` es lo que se guarda en la base y NO se puede cambiar a la ligera.** `BCP`, `BBVA` e
 * `INTERBANK` están escritos así porque es exactamente lo que ya tienen los 33 perfiles cargados
 * (28 + 3 + 2, verificado contra la base el 2026-09-03). Cambiar uno de esos tres partiría en dos
 * el mismo banco: los viejos con una forma y los nuevos con otra. `etiqueta` sí es libre — es solo
 * lo que lee la persona.
 *
 * El orden no es alfabético a propósito: arriba los cuatro que concentran casi todas las cuentas,
 * porque en un celular cada desplazamiento es una oportunidad de elegir mal.
 */
export interface Banco {
  /** Lo que se GUARDA. Estable: cambiarlo divide el histórico del mismo banco. */
  valor: string;
  /** Lo que se MUESTRA en el desplegable. */
  etiqueta: string;
}

/** Valor del centinela «Otro». NO se guarda nunca: si se elige, se guarda lo que la persona escriba. */
export const BANCO_OTRO = 'OTRO';

export const BANCOS: readonly Banco[] = [
  // Los cuatro de siempre.
  { valor: 'BCP', etiqueta: 'BCP — Banco de Crédito del Perú' },
  { valor: 'BBVA', etiqueta: 'BBVA' },
  { valor: 'INTERBANK', etiqueta: 'Interbank' },
  { valor: 'SCOTIABANK', etiqueta: 'Scotiabank' },
  // Resto de la banca múltiple.
  { valor: 'BANCO DE LA NACION', etiqueta: 'Banco de la Nación' },
  { valor: 'BANBIF', etiqueta: 'BanBif — Banco Interamericano de Finanzas' },
  { valor: 'MIBANCO', etiqueta: 'Mibanco' },
  { valor: 'PICHINCHA', etiqueta: 'Banco Pichincha' },
  { valor: 'FALABELLA', etiqueta: 'Banco Falabella' },
  { valor: 'RIPLEY', etiqueta: 'Banco Ripley' },
  { valor: 'GNB', etiqueta: 'Banco GNB' },
  { valor: 'ALFIN', etiqueta: 'Alfin Banco' },
  { valor: 'SANTANDER', etiqueta: 'Banco Santander Perú' },
  { valor: 'CITIBANK', etiqueta: 'Citibank del Perú' },
  // Financieras.
  { valor: 'CREDISCOTIA', etiqueta: 'CrediScotia Financiera' },
  { valor: 'COMPARTAMOS', etiqueta: 'Compartamos Financiera' },
  { valor: 'CONFIANZA', etiqueta: 'Financiera Confianza' },
  { valor: 'EFECTIVA', etiqueta: 'Financiera Efectiva' },
  { valor: 'OH', etiqueta: 'Financiera Oh!' },
  { valor: 'CREDINKA', etiqueta: 'Financiera Credinka' },
  // Cajas municipales — muy usadas en provincia, que es donde está media operación.
  { valor: 'CAJA AREQUIPA', etiqueta: 'Caja Arequipa' },
  { valor: 'CAJA HUANCAYO', etiqueta: 'Caja Huancayo' },
  { valor: 'CAJA PIURA', etiqueta: 'Caja Piura' },
  { valor: 'CAJA CUSCO', etiqueta: 'Caja Cusco' },
  { valor: 'CAJA TRUJILLO', etiqueta: 'Caja Trujillo' },
  { valor: 'CAJA SULLANA', etiqueta: 'Caja Sullana' },
  { valor: 'CAJA ICA', etiqueta: 'Caja Ica' },
  { valor: 'CAJA TACNA', etiqueta: 'Caja Tacna' },
  { valor: 'CAJA DEL SANTA', etiqueta: 'Caja del Santa' },
  { valor: 'CAJA MAYNAS', etiqueta: 'Caja Maynas' },
  { valor: 'CAJA PAITA', etiqueta: 'Caja Paita' },
  { valor: 'CAJA METROPOLITANA', etiqueta: 'Caja Metropolitana de Lima' },
];

/** `true` si el texto es exactamente uno de los valores de la lista. */
export function esBancoDeLaLista(texto: string): boolean {
  return BANCOS.some((b) => b.valor === texto);
}
